import type { NavGrid } from './nav-grid';
import type { Point2 } from './person-motion';

/**
 * Circulation entre les étages d'un site (ADR-017, démo) : les grilles de chaque étage
 * ({@link NavGrid}) reliées par les escaliers du GLB. Le personnage 3D change d'étage en montant
 * les marches, et la distance à pied d'un point à l'autre (portes et escalier compris) sert à
 * rejeter les points impossibles. Fonctions pures, sans three.js.
 *
 * Repère three.js du modèle : x, z à l'horizontale, y vers le haut, en mètres.
 */
export interface Point3 {
  x: number;
  y: number;
  z: number;
}

/** Ligne de marche d'un escalier, du plancher `lower` au plancher `upper` (y croissant). */
export interface StairLink {
  lower: number;
  upper: number;
  line: Point3[];
}

/** Même marge que `levelOf` : à 0,4 m d'un plancher, on est à cet étage. */
const LEVEL_MARGIN_M = 0.4;
/** Hauteur des tranches de marches moyennées en un point de la ligne de marche. */
const BIN_M = 0.25;
/** Tranches ignorées contre chaque plancher (poteau, palier, garde-corps de l'étage). */
const END_SKIP_M = 0.15;
/** Pas fait hors de l'escalier, au pied et en haut, dans le sens de la marche. */
const LANDING_M = 0.6;
/**
 * Une position mesurée entre deux étages n'est mise sur l'escalier qu'à cette distance au plus de la
 * marche à cette hauteur : un tracker porté à hauteur de poitrine, dans la pièce de l'escalier, a
 * aussi une hauteur « entre deux étages ».
 */
const STAIR_SNAP_M = 1.2;

/**
 * Ligne de marche d'un escalier du GLB (triangles d'un nœud `Stairs`) : centre des marches par
 * tranche de hauteur, prolongé d'un pas sur chaque plancher. Un lien par paire d'étages voisins
 * que l'escalier relie.
 */
export function stairsFromTriangles(tris: ArrayLike<number>, levels: number[]): StairLink[] {
  const ys = [...levels].sort((a, b) => a - b);
  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 1; i < tris.length; i += 3) {
    minY = Math.min(minY, tris[i]);
    maxY = Math.max(maxY, tris[i]);
  }
  const out: StairLink[] = [];
  for (let l = 0; l + 1 < ys.length; l++) {
    const a = ys[l];
    const b = ys[l + 1];
    if (minY > a + 0.3 || maxY < b - 0.3) {
      continue;
    }
    const steps: Point3[] = [];
    for (let lo = a + END_SKIP_M; lo < b - END_SKIP_M - 1e-9; lo += BIN_M) {
      const hi = Math.min(lo + BIN_M, b - END_SKIP_M);
      let n = 0;
      let sx = 0;
      let sy = 0;
      let sz = 0;
      for (let i = 0; i + 2 < tris.length; i += 3) {
        const y = tris[i + 1];
        if (y >= lo && y < hi) {
          n++;
          sx += tris[i];
          sy += y;
          sz += tris[i + 2];
        }
      }
      if (n) {
        steps.push({ x: sx / n, y: sy / n, z: sz / n });
      }
    }
    if (steps.length < 2) {
      continue;
    }
    const out1 = (from: Point3, to: Point3, y: number): Point3 => {
      const d = Math.hypot(to.x - from.x, to.z - from.z) || 1;
      return { x: to.x + ((to.x - from.x) / d) * LANDING_M, y, z: to.z + ((to.z - from.z) / d) * LANDING_M };
    };
    out.push({
      lower: a,
      upper: b,
      line: [out1(steps[1], steps[0], a), ...steps, out1(steps.at(-2)!, steps.at(-1)!, b)],
    });
  }
  return out;
}

/** Nœud du graphe : un point sur un étage (`level`) et / ou sur un escalier (`u` le long de sa ligne). */
interface Node {
  p: Point3;
  level: number | null;
  stair: { link: number; u: number } | null;
}

interface Route {
  path: Point3[];
  length: number;
}

export class NavGraph {
  private readonly levels: number[];

  constructor(
    levels: number[],
    private readonly gridAt: (floorY: number) => NavGrid | null,
    private readonly stairs: StairLink[]
  ) {
    this.levels = [...levels].sort((a, b) => a - b);
  }

  /**
   * Chemin à pied de `from` à `to` (départ exclu, arrivée incluse), par les portes et les escaliers ;
   * `null` s'il n'y en a pas. Un point mesuré entre deux étages, près d'un escalier, est posé sur
   * l'escalier à cette hauteur.
   */
  route(from: Point3, to: Point3, opts: { allowWallCrossing?: boolean } = {}): Point3[] | null {
    return this.solve(from, to, opts)?.path ?? null;
  }

  /** Longueur du chemin à pied (m), ou `null` s'il n'y en a pas. */
  distance(from: Point3, to: Point3, opts: { allowWallCrossing?: boolean } = {}): number | null {
    return this.solve(from, to, opts)?.length ?? null;
  }

  /** Point où montrer `p` : sur son étage, ou sur l'escalier s'il est mesuré entre deux étages. */
  place(p: Point3): Point3 {
    return this.node(p).p;
  }

  private solve(from: Point3, to: Point3, opts: { allowWallCrossing?: boolean }): Route | null {
    const nodes: Node[] = [this.node(from), this.node(to)];
    const [s, g] = nodes;
    // Même étage : chemin direct, sans détour par les escaliers.
    if (s.level !== null && s.level === g.level) {
      const direct = this.onLevel(s, g, opts);
      if (direct) {
        return direct;
      }
    }
    this.stairs.forEach((link, i) => {
      nodes.push({ p: link.line[0], level: link.lower, stair: { link: i, u: 0 } });
      nodes.push({ p: link.line.at(-1)!, level: link.upper, stair: { link: i, u: link.line.length - 1 } });
    });
    // Dijkstra sur quelques nœuds ; les arêtes d'étage (A*) ne sont calculées qu'au besoin.
    const dist = nodes.map(() => Infinity);
    const prev: { from: number; edge: Route }[] = nodes.map(() => ({ from: -1, edge: { path: [], length: 0 } }));
    const done = nodes.map(() => false);
    dist[0] = 0;
    for (;;) {
      let i = -1;
      for (let k = 0; k < nodes.length; k++) {
        if (!done[k] && dist[k] < Infinity && (i < 0 || dist[k] < dist[i])) i = k;
      }
      if (i < 0) {
        return null;
      }
      if (i === 1) {
        break;
      }
      done[i] = true;
      for (let j = 1; j < nodes.length; j++) {
        if (done[j] || j === i) continue;
        const edge = this.edge(nodes[i], nodes[j], opts);
        if (edge && dist[i] + edge.length < dist[j]) {
          dist[j] = dist[i] + edge.length;
          prev[j] = { from: i, edge };
        }
      }
    }
    const parts: Point3[][] = [];
    for (let k = 1; k !== 0; k = prev[k].from) {
      parts.unshift(prev[k].edge.path);
    }
    return { path: parts.flat(), length: dist[1] };
  }

  /** Arête directe entre deux nœuds : sur un même étage, ou le long d'un même escalier. */
  private edge(a: Node, b: Node, opts: { allowWallCrossing?: boolean }): Route | null {
    if (a.stair && b.stair && a.stair.link === b.stair.link) {
      return this.along(a, b);
    }
    if (a.level !== null && a.level === b.level) {
      return this.onLevel(a, b, opts);
    }
    return null;
  }

  private onLevel(a: Node, b: Node, opts: { allowWallCrossing?: boolean }): Route | null {
    const level = a.level as number;
    const grid = this.gridAt(level);
    const pts = grid?.findPath(flat(a.p), flat(b.p), opts);
    if (!pts) {
      return null;
    }
    const path = pts.map(q => ({ x: q.x, y: level, z: q.z }));
    return { path, length: length(a.p, path) };
  }

  /** Le long de la ligne de marche d'un escalier, d'un point à l'autre. */
  private along(a: Node, b: Node): Route {
    const line = this.stairs[a.stair!.link].line;
    const u1 = a.stair!.u;
    const u2 = b.stair!.u;
    const path: Point3[] = [];
    if (u2 > u1) {
      for (let i = Math.floor(u1) + 1; i < u2; i++) path.push(line[i]);
    } else {
      for (let i = Math.ceil(u1) - 1; i > u2; i--) path.push(line[i]);
    }
    path.push(pointAt(line, u2));
    return { path, length: length(a.p, path) };
  }

  /** Étage du point (à la marge près), sinon escalier à cette hauteur, sinon étage en dessous. */
  private node(p: Point3): Node {
    const level = this.levels.find(l => Math.abs(p.y - l) <= LEVEL_MARGIN_M);
    if (level !== undefined) {
      return { p: { x: p.x, y: level, z: p.z }, level, stair: null };
    }
    let best: Node | null = null;
    let bestD = STAIR_SNAP_M;
    this.stairs.forEach((link, i) => {
      if (p.y <= link.lower || p.y >= link.upper) return;
      const u = uAtHeight(link.line, p.y);
      const q = pointAt(link.line, u);
      const d = Math.hypot(q.x - p.x, q.z - p.z);
      if (d <= bestD) {
        bestD = d;
        best = { p: q, level: null, stair: { link: i, u } };
      }
    });
    if (best) {
      return best;
    }
    const below = this.levels.filter(l => l - LEVEL_MARGIN_M <= p.y).at(-1) ?? this.levels[0];
    return { p: { x: p.x, y: below, z: p.z }, level: below, stair: null };
  }
}

function flat(p: Point3): Point2 {
  return { x: p.x, z: p.z };
}

function length(from: Point3, path: Point3[]): number {
  let d = 0;
  let prev = from;
  for (const p of path) {
    d += Math.hypot(p.x - prev.x, p.y - prev.y, p.z - prev.z);
    prev = p;
  }
  return d;
}

function pointAt(line: Point3[], u: number): Point3 {
  const k = Math.min(Math.floor(u), line.length - 2);
  const f = u - k;
  const a = line[k];
  const b = line[k + 1];
  return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, z: a.z + (b.z - a.z) * f };
}

/** Position le long d'une ligne de marche (y croissant) à la hauteur `y`. */
function uAtHeight(line: Point3[], y: number): number {
  for (let k = 0; k + 1 < line.length; k++) {
    const a = line[k].y;
    const b = line[k + 1].y;
    if (y >= a && y <= b) {
      return k + (b > a ? (y - a) / (b - a) : 0);
    }
  }
  return y <= line[0].y ? 0 : line.length - 1;
}
